'use client';

import React from 'react';
import { ConfigProvider } from 'antd';
import { orionTheme } from './theme/orion-theme';

export interface OrionProviderProps {
  /**
   * Child components that will receive the Orion theme
   */
  children: React.ReactNode;
  
  /**
   * Optional theme overrides
   */
  theme?: Partial<typeof orionTheme>;
  
  /**
   * Locale configuration for internationalization
   */
  locale?: any;
}

/**
 * OrionProvider - Applies OpenStore Orion theme to all Ant Design components
 * 
 * @example
 * ```tsx
 * import { OrionProvider, Button, Card } from '@openstore/orion';
 * 
 * function App() {
 *   return (
 *     <OrionProvider>
 *       <Card title="Dashboard">
 *         <Button type="primary">Add Product</Button>
 *       </Card>
 *     </OrionProvider>
 *   );
 * }
 * ```
 */
export const OrionProvider: React.FC<OrionProviderProps> = ({ 
  children, 
  theme: themeOverrides,
  locale 
}) => {
  // Merge custom theme overrides with base Orion theme
  const finalTheme = themeOverrides 
    ? {
        ...orionTheme,
        token: {
          ...orionTheme.token,
          ...themeOverrides.token,
        },
        components: {
          ...orionTheme.components,
          ...themeOverrides.components,
        },
      }
    : orionTheme;

  return (
    <ConfigProvider theme={finalTheme} locale={locale}>
      {children}
    </ConfigProvider>
  );
};

OrionProvider.displayName = 'OrionProvider';